/* eslint-disable complexity */
/**
 * @typedef {import('./AgentCommunicationPreferencesComponent').Props} Props
 * @typedef {import('./AgentCommunicationPreferencesComponent').State} State
 * @typedef {import('formik').FormikProps<{ [key: string]: string }>} FieldProps
 */

import { translate } from '../../Internationalization';
import { withAgentService } from '../../../service-container';
import AgentCommunicationRadio from './AgentCommunicationRadio';
import BaseForm from '../../../shared/Forms/BaseForm';
import captureError from '../../../utils/captureError';
import flags from '../../../shared/Flags';
import FormErrorMessages from '../../../shared/Forms/Messages/FormErrorMessages';
import FormSuccessMessages from '../../../shared/Forms/Messages/FormSuccessMessages';
import paths from '../../../config/local/paths';
import React, { useEffect, useState } from 'react';
import SubmitButton from '../../../shared/Forms/Inputs/SubmitButton';
import UpgradeButton from '../../../shared/Forms/Inputs/UpgradeButton';
import useGetAgentSettings from '../../../Hooks/Agent/Getters/useGetAgentSettings/useGetAgentSettings';
import useIsGated from '../../../Hooks/Shared/useIsGated/useIsGated';
import useSettings from '../../../Hooks/Shared/useSettings/useSettings';

const { marketActivityEmail, sendMarketActivity } = flags.agent;

/**
 * @param {Props} props
 * @returns {JSX.Element | null}
 */
function AgentCommunicationPreferences(props) {
	const [error, setError] = useState(/** @type {string|null}*/ (null));
	const [isGettingData, setIsGettingData] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [marketActivity, setMarketActivity] = useState(false);
	const [success, setSuccess] = useState(/** @type {string|null}*/ (null));
	const { features } = useSettings();
	const agentGated = useIsGated(marketActivityEmail);
	const hasMarketActivity = features.get(sendMarketActivity) ?? false;
	const agentSettings = useGetAgentSettings(!hasMarketActivity);

	useEffect(() => {
		if (agentGated.isGettingData || agentSettings.isGettingData) {
			return;
		}

		setIsGettingData(false);

		if (!hasMarketActivity || agentGated.isGated) {
			return;
		}

		setMarketActivity(agentSettings?.data?.campaigns?.marketActivity);
	}, [agentGated, agentSettings]);

	if (!hasMarketActivity) {
		return null;
	}

	/**
	 * @param {React.ChangeEvent<HTMLInputElement>} event
	 * @returns {void}
	 */
	function handleChange(event) {
		const marketActivityValue = event.target.value === 'true';
		if (marketActivityValue === marketActivity) {
			return;
		}

		setMarketActivity(marketActivityValue);
	}

	async function handleSubmit() {
		setSuccess(null);
		setError(null);

		try {
			await props.agentService.updateEmailCommunicationPreferences({
				marketActivity,
			});

			setSuccess(translate('settings.communications.messages.success'));
		} catch (exception) {
			captureError(exception);
			setError(translate('settings.communications.messages.error'));
		} finally {
			setIsSubmitting(false);
		}
	}

	/** @returns {JSX.Element|null} */
	function renderSuccess() {
		if (success) {
			return <FormSuccessMessages messages={success} />;
		}

		if (error || agentSettings.error) {
			return (
				<FormErrorMessages messages={error || agentSettings.error} />
			);
		}

		return null;
	}

	function submitBtn() {
		if (agentGated.isGated && !isGettingData) {
			return <UpgradeButton to={paths.app.agent.subscription.root} />;
		}

		return (
			<SubmitButton disabled={isGettingData}>
				{translate('global.forms.buttons.save')}
			</SubmitButton>
		);
	}

	return (
		<section>
			<h2 className="h-ms-5 mb-6">
				{translate('settings.communications.title')}
			</h2>

			{renderSuccess()}

			<BaseForm
				className="mb-8"
				enableReinitialize={true}
				initialValues={{
					marketActivity:
						marketActivity && !agentGated.isGated
							? 'true'
							: 'false',
				}}
				noRequiredText={true}
				onSubmit={handleSubmit}
			>
				<fieldset
					className="flex flex-col gap-8"
					key={`campaigns-toggle-market_activity`}
				>
					<AgentCommunicationRadio
						disabled={agentGated.isGated || !!error || isSubmitting}
						isGettingData={isGettingData}
						onChange={handleChange}
						onSubmit={handleSubmit}
					/>
				</fieldset>

				{submitBtn()}
			</BaseForm>
		</section>
	);
}

export default withAgentService(AgentCommunicationPreferences);
